import { useState, useEffect } from 'react';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Input,
  InputTypes,
  RadioButton,
  MLPVersion,
  Theme,
  ButtonStyles,
  TextArea,
  DatePicker,
  TableContainer,
  Card,
  Modal,
  Button,
  ButtonTypes,
  ButtonSize,
} from '../../../../ui/components';
import ShareDrawer from '../../../../ui/components/DrawerAlt/ShareDrawer';
import { LoggerService, dateTimeToCustomFormat, formatISODate, getTranslatedMessage } from '../../../../ui/utils';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { ParticipantDataStructure, TeamData } from '../teamsListConfig';
import getParticipantListConfig from '../participantListConfig';
import TeamService from '../../../../sdk/services/teamService';
import AuditLogService from '../../../../sdk/services/auditLogService';
import ParticipantService from '../../../../sdk/services/participantService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { useAPICall } from '../../../../hooks';
import styles from '../index.module.scss';
import AuditHistoryAccordion from '../../audit-log/auditLogAccordion';

export const initTeamForm: TeamData = {
  teamId: null,
  teamName: '',
  description: '',
  campaignId: '',
  campaignRecId: null,
  campaignName: '',
  campaignStart: '',
  campaignEnd: '',
  teamLeader: '',
  coachId: '',
  coachName: '',
  teamStartDate: '',
  teamEndDate: '',
  status: '',
  participationType: '',
};

export const AdminTeamEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const apiCall = useAPICall();
  const id = searchParams.get('');
  const [teamData, setTeamData] = useState<TeamData>(initTeamForm);
  const [originalTeamData, setOriginalTeamData] = useState<TeamData>(initTeamForm);
  const [selectedRow, setSelectedRow] = useState<ParticipantDataStructure | null>(null);
  const [participantList, setParticipantList] = useState<ParticipantDataStructure[]>([]);
  const [displayEmpty, setDisplayEmpty] = useState(false);
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);
  const [displayAuditEmpty, setAuditDisplayEmpty] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [joinTeamLink, setJoinTeamLink] = useState<string>('');

  useEffect(() => {
    (async () => {
      const result = await apiCall(TeamService.getTeamDetails, `${id}`);
      if (result.status === 200) {
        let data = result.data.data;
        setTeamData(data ?? initTeamForm);
        setOriginalTeamData(data ?? initTeamForm);
        if (data.campaignId && data.teamId) {
          const payload = {
            campaignId: data.campaignId,
            teamId: data.teamId?.toString(),
          };
          const result = await apiCall(ParticipantService.getTeamParticipants, payload);
          if (result.status === 200) {
            const participantsWithAcknowledgement = result.data.data?.map((participant: DynamicObjectInterface) => ({
              ...participant,

              acknowledgement: participant.weightIn?.acknowledgeDateTime && participant.weightOut?.acknowledgeDateTime,
              orderStatus: participant?.purchase[0]?.orderStatus === 1 ? 'YES' : 'NO',
              isIndividualAchieved:
                participant?.isIndividualAchieved === 1
                  ? 'YES'
                  : participant.isIndividualAchieved === 0
                    ? 'NO'
                    : 'PENDING',
            }));

            setParticipantList(participantsWithAcknowledgement);
            setDisplayEmpty(participantsWithAcknowledgement.length === 0);

            // allocateTeamRewardsCheck('weightIn', data.campaignId, data.teamId);
            // allocateTeamRewardsCheck('weightOut', data.campaignId, data.teamId);
          } else {
            setDisplayEmpty(true);
            LoggerService.log('Failed to retrieve team detail', result.status);
          }
        }
      } else {
        LoggerService.log('Failed to retrieve team detail');
      }
    })();
  }, [id]);

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setTeamData(prevKpiData => ({
      ...prevKpiData!,
      [name]: value,
    }));
  };

  const generateJoinTeamLink = () => {
    const params = `${teamData.campaignRecId},${teamData.teamId},0`;
    const encodedParam = btoa(params);
    if (teamData.teamId && teamData.campaignRecId) {
      setJoinTeamLink(`${window.location.protocol}//${window.location.host}?source=email&t=${encodedParam}`);
      setShowShareDrawer(true);
    }
  };

  const onSave = async () => {
    const payload = {
      teamName: teamData?.teamName,
      description: teamData?.description,
      teamLeader: teamData?.teamLeader,
      coachId: teamData?.coachId ? teamData?.coachId.toString() : '',
      teamStartDate: teamData?.teamStartDate ? moment(teamData?.teamStartDate).format('YYYY-MM-DD') : null,
      teamEndDate: teamData?.teamEndDate ? moment(teamData?.teamEndDate).format('YYYY-MM-DD') : null,
      status: teamData?.status,
      campaignId: teamData?.campaignId,
      modifiedBy: '',
    };

    const result = await apiCall(TeamService.updateTeam, `${id}`, payload);
    if (result.status === 200) {
      navigate(`/admin/challenge/manage-team/list`);
      adminToast({
        message: t('admin.teamEdit.teamUpdateSuccess'),
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: getTranslatedMessage(t, result?.data?.message ?? 'admin.teamEdit.teamUpdateError'),
        theme: Theme.ERROR,
      });
    }
  };

  const teamDetailsBtnConfig = [
    {
      title: t('admin.teamEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        let redirectFrom = sessionStorage.getItem('redirectFrom');
        if (redirectFrom) {
          sessionStorage.removeItem('redirectFrom');
          navigate(redirectFrom);
        } else {
          navigate(`/admin/challenge/manage-team/list`);
        }
      },
    },
    {
      title: t('admin.teamEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: !teamData.teamName,
      onClick: onSave,
    },
  ];

  const participantBtnConfig = [
    {
      title: t('admin.teamEdit.addParticipant'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/challenge/manage-team/edit/add-participant?=${teamData.teamId}`);
      },
    },
    {
      title: t('admin.teamEdit.shareLink'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        generateJoinTeamLink();
      },
    },
  ];

  const onEdit = (id: ParticipantDataStructure) => {
    setSelectedRow(id);
    navigate(
      `/admin/challenge/manage-team/edit/participant-details?campaignId=${id.campaignId}&teamId=${id.teamId}&userId=${id.userId}`,
    );
  };

  const onDelete = (data: ParticipantDataStructure) => {
    setSelectedRow(data);
    setShowDelete(true);
  };

  const onDeleteAction = async () => {
    const parti = {
      teamName: teamData?.teamName,
      teamId: teamData?.teamId,
      campaignId: teamData?.campaignId,
      name: selectedRow?.name,
      loginId: selectedRow?.loginId,
    };

    const result = await apiCall(ParticipantService.deleteTeamParticipant, `${selectedRow?.recId}`, parti);
    if (result.status === 200) {
      LoggerService.log('Participant deleted successfully');
      adminToast({
        message: 'Participant deleted successfully',
        theme: Theme.SUCCESS,
      });
      window.location.reload();
    } else {
      LoggerService.log('Error deleting Participant');
      adminToast({
        message: 'Error deleting Participant',
        theme: Theme.ERROR,
      });
    }
    setShowDelete(false);
  };

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        if (teamData?.campaignId && teamData?.teamId) {
          const payload = {
            campaignId: teamData?.campaignId,
            teamId: teamData?.teamId,
          };

          const historyLogResponse = await apiCall(AuditLogService.fetchLog, payload);
          const historyLogResult = historyLogResponse.data;

          setAuditHistoryLog(historyLogResult.data || []);
          setAuditDisplayEmpty(historyLogResult.data.length === 0);
        }
      } catch (error) {
        LoggerService.log('Failed to retrieve audit history log', error);
      }
    };

    fetchAuditLogs();
  }, [teamData?.campaignId, teamData?.teamId]);

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.teams.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.teams.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <>
            <div className={styles['edit-team-delete-title']}>{t('admin.teams.deleteParticipant')}</div>
            <span>ABO ID:{selectedRow?.loginId ?? '-'}</span>
            <br />
            <span> Name: {selectedRow?.name ?? '-'}</span>
          </>
        </Modal>
      )}
    </>
  );

  const sharePrompt = (
    <>
      {showShareDrawer && (
        <ShareDrawer
          open={!!joinTeamLink}
          onClose={() => setShowShareDrawer(false)}
          title="Share Via"
          programMessage={`You have been invited to join ${teamData?.campaignName} at ${joinTeamLink}`}
          programUrl={joinTeamLink}
          channels={process.env.REACT_APP_COUNTRY === 'PH' ? ['FaWhatsapp', 'FaViber', 'FaEnvelope','FaLink'] : ['FaWhatsapp','FaEnvelope','FaLink']}
          programTitle=""
        />
      )}
    </>
  );

  return (
    <div className={styles['edit-team']}>
      <Card title={t('admin.teamEdit.title')} button={teamDetailsBtnConfig}>
        <div className={styles['edit-team-details']}>
          <div className={`${styles['edit-team-details-form']} ${styles['campaign-info']}`}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'campaignId'}
              disabled={true}
              label={t('admin.teams.campaignId')}
              value={teamData?.campaignId ?? ''}
              mlpVersion={MLPVersion.ONE}
            />
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'campaignName'}
              disabled={true}
              label={t('admin.teams.campaignName')}
              value={teamData?.campaignName ?? ''}
              mlpVersion={MLPVersion.ONE}
            />
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'campaignPeriod'}
              disabled={true}
              label={t('admin.teams.campaignPeriod')}
              value={`${dateTimeToCustomFormat(teamData?.campaignStart)} - ${dateTimeToCustomFormat(
                teamData?.campaignEnd,
              )}`}
              mlpVersion={MLPVersion.ONE}
            />
          </div>
          <hr className={styles['horizontal-divider']} />
          <div className={`${styles['edit-team-details-form']}`}>
            <div className={styles['form-verticle-spacing']}>
              <Input
                key={id}
                type={InputTypes.TEXT}
                name={'teamName'}
                label={`${t('admin.teams.teamName')}*`}
                placeholder={`${t('admin.teams.teamName')}`}
                value={teamData?.teamName}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChange('teamName', e.target.value);
                }}
              />
              <Input
                key={id}
                type={InputTypes.TEXT}
                name={'teamName'}
                label={`${t('admin.teams.coach')}*`}
                placeholder={`${t('admin.teams.teamName')}`}
                value={teamData?.coachId + ' - ' + teamData?.coachName}
                mlpVersion={MLPVersion.ONE}
                disabled={true}
                onChange={e => {
                  handleInputChange('coachId', e.target.value);
                }}
              />
              <div className={styles['details-bottom-info-item']}>
                <div className={styles['label']}>Participation Type</div>
                <p className={styles['radio-button-container']}>
                  <RadioButton
                    label={t('dashboard.create-team.input.team')}
                    checked={!teamData?.participationType || teamData?.participationType === 'team'}
                    disabled={true}
                  />
                  <RadioButton
                    label={t('dashboard.create-team.input.individual')}
                    checked={teamData?.participationType === 'ind'}
                    disabled={true}
                  />
                </p>
              </div>
            </div>
            <div className={styles['form-verticle-spacing']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                label={t('admin.teams.description')}
                placeholder={t('admin.teams.description')}
                value={teamData?.description}
                onChange={e => {
                  handleInputChange('description', e.target.value);
                }}
              />
              <div className={styles['input-period']}>
                <DatePicker
                  showDateContainer
                  containerWidth="450px"
                  inputContainerHeight="36px"
                  calendarHeading={t('admin.teams.teamStartDate')}
                  value={teamData?.teamStartDate ? new Date(teamData?.teamStartDate) : undefined}
                  onChange={date => handleInputChange('teamStartDate', formatISODate(date ?? ''))}
                  inputLabel={t('admin.teams.teamStartDate')}
                  placeholder={t('admin.teams.teamStartDate')}
                />
                <DatePicker
                  showDateContainer
                  inputContainerHeight="36px"
                  containerWidth="450px"
                  calendarContainerStyling={{ right: '0px' }}
                  calendarHeading={t('admin.teams.teamEndDate')}
                  value={teamData?.teamEndDate ? new Date(teamData?.teamEndDate) : undefined}
                  onChange={date => handleInputChange('teamEndDate', formatISODate(date ?? ''))}
                  inputLabel={t('admin.teams.teamEndDate')}
                  placeholder={t('admin.teams.teamEndDate')}
                />
              </div>
              <div className={styles['input-status']}>
                <span className={styles['input-label']}>{t('admin.teams.status')}</span>
                <div className={styles['input-radio']}>
                  <RadioButton
                    label="Active"
                    checked={teamData?.status === 'ACTIVE'}
                    value={'ACTIVE'}
                    onChange={e => handleInputChange('status', e.target.value)}
                    disabled={originalTeamData?.status === 'INACTIVE'}
                  />
                  <RadioButton
                    label="Inactive"
                    checked={teamData?.status === 'INACTIVE'}
                    value={'INACTIVE'}
                    onChange={e => handleInputChange('status', e.target.value)}
                    disabled={originalTeamData?.status === 'INACTIVE'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Card title={t('admin.teamEdit.participant')} button={participantBtnConfig}>
        {displayEmpty ? (
          <div className={styles['no-results-container']}>
            <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
          </div>
        ) : (
          <div>
            <TableContainer
              dataSource={participantList}
              getColumns={getParticipantListConfig}
              rowKey="name"
              selected={selectedRow?.name}
              cb={{ onEdit, onDelete }}
              onRowClick={row => {
                setSelectedRow(row);
              }}
            />
          </div>
        )}
      </Card>
      <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={displayAuditEmpty} />
      {deletePrompt}
      {sharePrompt}
    </div>
  );
};
